import useSocketReconnect from '../../utils/useSocketReconnect';
import { useRef, useState } from "react"
import { useEffect } from "react"
import { isMobile } from "react-device-detect"
import './FormEditorStyles.css'
import { fetchAPI, getSubdomain, postAPI } from "../../utils/utils"
import { getStripeCommissionInfoSync, prefetchCommissionInfo } from "../../utils/stripeCommission"
import { can } from "../../utils/permissions"
import Popup from "../other/Popup"
import Pressable from "../other/Pressable"
import FormImageOCRReader from "./utils/FormImageOCRReader"
import useScheduler from "./utils/useScheduler"
import PopupFormImage from "./components/PopupFormImage"
import { isResponseSpam, includeSpamWords, exactSpamWords } from "../../utils/spamDetection"
import { useMemo } from "react"
import { getCastellerDisplayName } from "../../utils/castellerName"
import { formatMoney } from "../../utils/money"
import { canShareExternalForm } from "./formShareAccess"
import { isFormElementVisible } from "./utils/formVisibility"
import { membersNamed, possibleDuplicates } from "./utils/guestDuplicates"
import { guestLabel, guestTypedName, useFormGuests } from "./utils/formGuests"

const COLLA = getSubdomain()

const getQuestionLabel = (element) => {
    const baseQuestion = element?.content?.question || '';
    const suffix = element?.deleted ? ' (Eliminada)' : '';
    return `${baseQuestion}${suffix}`;
}

// Check if a deleted element has any responses
const hasAnyResponse = (element, responses) => {
    if (!element?.deleted) return true; // Non-deleted elements always shown
    
    return responses.some(response => {
        const value = response?.[element.id];
        if (value === undefined || value === null || value === '') return false;
        
        // For ticket-v2 / checkbox: check if object/array has any non-zero values
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return Object.values(value).some(v => v > 0);
        }
        
        // For multiple-choice/ticket/assistance: any numeric value counts (including 0)
        if (typeof value === 'number') return true;
        
        // For short-answer/paragraph: non-empty string
        if (typeof value === 'string') return value.trim() !== '';
        
        return Boolean(value);
    });
}

function FormImage({ id, evId, user, width='auto', height='auto' }) {
  const [imgURL, setImgURL] = useState(null)

  useEffect(() => {
    fetchAPI(`/form_image_url/${evId}/${id}/${user}`, (res) => {
      fetch(res?.url)
        .then(res => res.text())
        .then(url => url.indexOf('Error') > 0 ? false : url)
        .then(setImgURL)
    }, false, false)
  }, [id, evId, user])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={
          imgURL === null ? 'https://retchhh.files.wordpress.com/2015/03/loading1.gif' :
          !imgURL ? "https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png" :
          imgURL
        }
        alt="Carregant..."
        width={width}
        height={height}
      />
    </div>
  )
}

function FormImagePreview({ noVe, showNoVincs, setFocusedResponseInfo, setModalIsClosed, id, evId, user, name, recognize, showRecogintion, response, form, element, statusPagaments, setStatusPagaments, socket, socialMediaData }) {
  const [imgURL, setImgURL] = useState(null)

  useEffect(() => {
    fetchAPI(`/form_image_url/${evId}/${id}/${user}`, (res) => {
      fetch(res?.url)
        .then(res => res.text())
        .then(url => url.indexOf('Error') > 0 ? false : url)
        .then(setImgURL)
    }, false, false)
  }, [id, evId, user])

  const openModal = () => {
    setFocusedResponseInfo({
      name,
      user,
      response,
      element,
    })

    setModalIsClosed(false);
  }

  const closeModal = () => {
    setModalIsClosed(true);
  }

  const totalPaid = form.elements
    .filter(el => el.type === 'ticket-v2' || el.type === 'ticket-v2-single')
    .reduce((total, element) => {
      if (response?.[element.id]) {
        return total + element.content.options.reduce((subtotal, option, index) => {
          if (option.hidden) return subtotal;
          const quantity = response?.[element.id]?.[index] || 0;
          return subtotal + (option.price * quantity);
        }, 0);
      }
      return total;
    }, 0);

  const telegramContactRaw = socialMediaData?.[user]?.telegram?.trim();
  const telegramContact = !telegramContactRaw ? null : ('@' + telegramContactRaw?.replace(/^@/, ''));
  const telegramUrl = telegramContact
    ? telegramContact.startsWith('http')
      ? telegramContact
      : `https://t.me/${telegramContact.replace(/^@/, '')}`
    : null;

  return !(!showNoVincs && noVe) && (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <FormImageOCRReader
          imgURL={imgURL}
          showRecogintion={showRecogintion}
          recognize={recognize}
          openModal={openModal}
        >
          <img
            src={
              imgURL === null ? 'https://i.giphy.com/media/3oEjI6SIIHBdRxXI40/giphy.webp' :
              !imgURL ? "https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png" :
              imgURL
            }
            alt="Sense imatge"
            width={100}
            height={100}
            style={{
              objectFit: 'cover',
              borderRadius: 10,
              // filter: showRecogintion ? 'blur(5px)' : 'none',
            }}
            onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png"; }}
          />
        </FormImageOCRReader>
        <div
          style={{
            width: 100,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: 5,
          }}
        >
          <div
            style={{
              fontSize: 12,
              backgroundColor:
                noVe ? 'rgb(255, 168, 168)' :
                (imgURL !== null && !imgURL) ? 'rgb(255, 220, 168)' :
                'transparent',
              borderRadius: 5,
              padding: 5,
            }}
          >
            {name}
            { totalPaid > 0 && `: ${formatMoney(totalPaid)}€` }
          </div>
        </div>
      </div>
      {
        element?.isComprovant === true && (
          <BotonsHaPagat
            maxWidth={100}
            response={response}
            form={form}
            element={element}
            statusPagaments={statusPagaments}
            setStatusPagaments={setStatusPagaments}
            user={user}
            socket={socket}
          />
        )
      }
      {
        element?.isComprovant === true && telegramContact && COLLA === "arreplegats" && (
          <div style={{ maxWidth: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 5, gap: 5 }}>
            {
              <a
                href={telegramUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 5,
                  borderRadius: 5,
                  border: '1px solid #0088cc',
                  backgroundColor: '#eee',
                  textDecoration: 'none',
                  width: '80%'
                }}
              >
                <div style={{ display: 'flex', gap: 5, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: 10, cursor: 'pointer', color: '#333', textAlign: 'center' }}>{telegramContact}</div>
                </div>
              </a>
            }
          </div>
        )
      }
    </>
  )
}

// Guests who answered without an account have ids like guest_<hex>, so ids are
// compared as text: parseInt would turn every guest into NaN.
const samePerson = (a, b) => a != null && b != null && String(a) === String(b)
const isGuestRow = row => String(row?.['casteller-id']).startsWith('guest_')

export function BotonsHaPagat({ maxWidth, user, response, form, element, statusPagaments, setStatusPagaments, socket }) {
  const paidTickets = form.elements
    .filter(element => element.type === 'ticket')
    .filter(element => response[element.id] === 0)
      .map(element => ({
      ...element,
      quantity: 1,
      title: getQuestionLabel(element),
    }))
    .concat(form.elements
      .filter(element => element.type === 'ticket-v2' || element.type === 'ticket-v2-single')
      .map(element => Object.entries(response?.[element.id] || {})
        ?.filter(([id, quantity]) => quantity > 0)
        ?.filter(([id]) => element.content?.options?.[id])
        ?.map(([id, quantity]) => ({
          ...element,
          id: `${element.id}_${id}`,
          price: element.content.options[id].price,
          title: element.content.options[id].text,
          quantity,
        }))
      )
      .flat()
    )

  const statusTickets = paidTickets.reduce((acc, ticket) => {
    const statusOfUser = statusPagaments?.[ticket.id]?.find(status => samePerson(status['casteller-id'], user));
    return { ...acc, [ticket.id]: statusOfUser?.status };
  }, {});

  const colorByStatus = (status) => {
    switch (status) {
      case 'Entregat':
        return '#6BA8D9';
      case 'Pagat':
        return '#68C285';
      case 'Demanat':
        return '#E69C55';
      case 'No demanat':
        return '#F27171';
      default:
        return '#eee';
    }
  }

  const updatePagamentStatusLocal = (prodId, nextStatus) => {
    setStatusPagaments(prev => {
      const currentStatuses = prev[prodId] || [];
      const updatedStatuses = currentStatuses.some(status => samePerson(status['casteller-id'], user))
        ? currentStatuses.map(status => samePerson(status['casteller-id'], user) ? { ...status, status: nextStatus } : status)
        : [...currentStatuses, { 'casteller-id': user, status: nextStatus }];

      return { ...prev, [prodId]: updatedStatuses };
    });
  };

  const set2Pagat = (prodId) => {
    const statusOfUser = statusPagaments?.[prodId]?.find(status => samePerson(status['casteller-id'], user));
    const status = statusOfUser?.status || 'No demanat';
    const nextStatus = status === 'Pagat' ? 'Demanat' : 'Pagat';

    socket.emit('.set_pagament_status', user, prodId, nextStatus);
    updatePagamentStatusLocal(prodId, nextStatus);
  }

  return paidTickets.length > 0 && (
    <div style={{ maxWidth, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 5, gap: 5, }}>
      {
        paidTickets.map((ticket, index) => (
          <Pressable onClick={() => set2Pagat(ticket.id)} key={ticket.id} style={{ display: 'flex', flexDirection: 'column', padding: 5, borderRadius: 5, backgroundColor: colorByStatus(statusTickets?.[ticket.id]) }}>
            <div style={{ display: 'flex', gap: 5, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <div style={{ fontSize: 10, cursor: 'pointer' }}>
                {
                  ['No demanat', 'Demanat'].includes(statusTickets?.[ticket.id]) ? '+' :
                  '✓'
                }
              </div>
              <div style={{ fontSize: 10, cursor: 'pointer' }}>{ticket.title}<b>{ ticket.quantity > 1 ? ` x ${ticket.quantity}` : ''}</b></div>
              <div style={{ fontSize: 10, cursor: 'pointer' }}>{formatMoney(ticket.price * ticket.quantity)}€</div>
            </div>
          </Pressable>
        ))
      }
    </div>
  )
}

function openGoogleSheet(url) {
  // Create an anchor element
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  // Add the link to the document
  document.body.appendChild(link);

  // Simulate a click on the link
  link.click();

  // Remove the link from the document
  document.body.removeChild(link);
}

const ShareExcel = ({ socket, selectedEvent, emailList, setEmailList, sendToExcel, responses, excelUrl, loadingExcel, form }) => {
    useSocketReconnect(socket, () => {
        if (selectedEvent) socket.emit('.request_shared_emails', selectedEvent);
    });

  const [email, setEmail] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && !emailList.includes(email)) {
      setEmailList([...emailList, email]);
      setEmail('');

      socket.emit('.share_google_sheet', selectedEvent, [email])
    }
  };

  const styles = {
    container: {
      backgroundColor: '#f8f9fa',
      borderRadius: 8,
      padding: 20,
    },
    success_btn: {
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: 4,
      padding: '8px 20px',
      cursor: 'pointer',
      fontSize: 14,
      marginBottom: 12,
    },
    form: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 12,
    },
    input: {
      flex: 1,
      padding: '8px 12px',
      borderRadius: 4,
      border: '1px solid #ddd',
      fontSize: 14,
      minWidth: 0,
    },
    button: {
      backgroundColor: '#77A9C5',
      color: 'white',
      border: 'none',
      borderRadius: 4,
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: 14,
      whiteSpace: 'nowrap',
    },
    ul: {
      listStyleType: 'none',
      paddingLeft: 0,
      margin: 0,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
    },
    li: {
      backgroundColor: '#ffc107',
      color: 'white',
      padding: '4px 10px',
      borderRadius: 4,
      fontSize: '0.85rem',
    },
  };
  
  useEffect(() => {
    if (!selectedEvent) return;

    const refreshSharedEmails = () => socket.emit('.request_shared_emails', selectedEvent);
    const handleSharedEmails = (emails) => {
      setEmailList(emails || []);
    };

    refreshSharedEmails();

    socket.on('.google_sheet_shared', refreshSharedEmails);
    socket.on('.shared_emails', handleSharedEmails);

    return () => {
      socket.off('.google_sheet_shared', refreshSharedEmails);
      socket.off('.shared_emails', handleSharedEmails);
    }
  }, [socket, selectedEvent]);

  return form && responses && responses.length > 0 && (
    <div style={styles.container}>
      <h3 style={{ margin: '0 0 10px 0' }}>Google Sheets</h3>
      <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#555' }}>
        Comparteix les respostes del formulari a un Google Sheet amb altres persones.
      </p>
      {excelUrl && (
        <button style={styles.success_btn} onClick={() => openGoogleSheet(excelUrl)}>
          Obre a Google Sheets
        </button>
      )}
      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          type="email"
          id="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          placeholder="Correu electrònic"
          required
        />
        <button type="submit" style={styles.button}>
          Comparteix
        </button>
      </form>
      {emailList.length > 0 && (
        <ul style={styles.ul}>
          {emailList.map((email) => (
            <li key={email} style={styles.li}>
              {email}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// What someone's answers add up to, from the form's prices
const totalToPayOf = (form, response) => {
  const tickets = form
    .elements
    .filter(element => element.type === 'ticket')

  const totalPaid = tickets
    .filter(ticket => response[ticket.id] === 0)
    .map(ticket => ticket.price)
    .map(price => parseFloat(price))
    .reduce((a, b) => a + b, 0)

  const ticketsv2 = form
    .elements
    .filter(element => element.type === 'ticket-v2' || element.type === 'ticket-v2-single')

  const totalPaidV2 = ticketsv2
    .map(ticket => Object.entries(response?.[ticket.id] || {}).reduce((acc, [id, quantity]) => acc + quantity * parseFloat(ticket.content.options[id].price), 0))
    .reduce((a, b) => a + b, 0)

  return totalPaid + totalPaidV2
}

const Summary = ({ isPublic, showRecogintion, setShowRecogintion, recognize, assistenciesEvent, statusPagaments, setStatusPagaments, selectedEvent, guestMotes, guestDuplicates = new Set(), form, responses, userIds, castellersInfo, setFilterText, setResponseIdx, socket, socialMediaData, stripePayments }) => {
  const [showNoVincs, setShowNoVincs] = useState(true);
  
  const filterOutNoVincs = (userId) => {
    return assistenciesEvent
      ?.find(assist => parseInt(assist.user) === parseInt(userId))
      ?.assistencia === 'No vinc' ? false : true;
  }

  const filterInNoVincs = (userId) => {
    return assistenciesEvent
      ?.find(assist => parseInt(assist.user) === parseInt(userId))
      ?.assistencia === 'No vinc' ? true : false;
  }

  const totalPaidPerResponse = (response) => totalToPayOf(form, response)

  const summarizeMultipleChoice = (element) => {
    // Defensive check for element structure
    if (!element?.content?.options || !Array.isArray(element.content.options)) {
      console.warn(`Invalid element structure for ${element?.id}: missing or invalid options array`);
      return [];
    }
    
    const summary = Array.from({ length: element.content.options.length }, () => ({
      vincs: 0,
      noVincs: 0,
    }));    

    responses.forEach((response, k) => {
      const choice = response[element.id];
      if (choice !== undefined && choice in summary) {
        // Add bounds checking to prevent array access errors
        if (choice < 0 || choice >= element.content.options.length) {
          console.warn(`Invalid choice index ${choice} for element ${element.id}. Expected 0-${element.content.options.length - 1}. Response from user ${userIds[k] || 'unknown'}`);
          return; // Skip this invalid response
        }
        
        if (filterInNoVincs(userIds[k])) {
          ++summary[choice].noVincs
        } else {
          ++summary[choice].vincs
        }
      }
    });
    return summary;
  };

  const summarizeCheckbox = (element) => {
    // Defensive check for element structure
    if (!element?.content?.options || !Array.isArray(element.content.options)) {
      console.warn(`Invalid element structure for ${element?.id}: missing or invalid options array`);
      return [];
    }
    
    const summary = Array.from({ length: element.content.options.length }, () => ({
      vincs: 0,
      noVincs: 0,
    }));
    
    responses.forEach((response, k) => {
      const checkedOptions = response[element.id];
      if (checkedOptions) {
        Object.entries(checkedOptions).forEach(([index, isChecked]) => {
          if (isChecked) {
            const optionIndex = parseInt(index);
            // Add bounds checking to prevent array access errors
            if (optionIndex < 0 || optionIndex >= element.content.options.length) {
              console.warn(`Invalid option index ${optionIndex} for element ${element.id}. Expected 0-${element.content.options.length - 1}. Response from user ${userIds[k] || 'unknown'}`);
              return; // Skip this invalid option
            }
            
            if (filterInNoVincs(userIds[k])) {
              ++summary[optionIndex].noVincs
            } else {
              ++summary[optionIndex].vincs
            }
          }
        });
      }
    });
    return summary;
  };  

  const summarizeTicketV2 = (element) => {
    // Defensive check for element structure
    if (!element?.content?.options || !Array.isArray(element.content.options)) {
      console.warn(`Invalid element structure for ${element?.id}: missing or invalid options array`);
      return [];
    }
    
    const summary = Array.from({ length: element.content.options.length }, () => ({
      vincs: 0,
      noVincs: 0,
      total: 0,
      totalPrice: 0,
      users: [],
    }));

    responses.forEach((response, k) => {
      const tickets = response[element.id];
      if (tickets && Array.isArray(tickets)) {
        tickets.forEach((count, index) => {
          if (count > 0) {
            // Add bounds checking to prevent array access errors
            if (index < 0 || index >= element.content.options.length) {
              console.warn(`Invalid ticket index ${index} for element ${element.id}. Expected 0-${element.content.options.length - 1}. Response from user ${userIds[k] || 'unknown'}`);
              return; // Skip this invalid ticket
            }
            
            const price = parseFloat(element.content.options[index].price);
            if (filterInNoVincs(userIds[k])) {
              summary[index].noVincs += count;
            } else {
              summary[index].vincs += count;
            }
            summary[index].total += count;
            summary[index].totalPrice += count * price;
            summary[index].users.push({ userId: userIds[k], count, userIndex: k });
          }
        });
      } else if (tickets && typeof tickets === 'object') {
        Object.entries(tickets).forEach(([index, count]) => {
          if (count > 0) {
            const optionIndex = parseInt(index);
            // Add bounds checking to prevent array access errors
            if (optionIndex < 0 || optionIndex >= element.content.options.length) {
              console.warn(`Invalid ticket option index ${optionIndex} for element ${element.id}. Expected 0-${element.content.options.length - 1}. Response from user ${userIds[k] || 'unknown'}`);
              return; // Skip this invalid ticket option
            }
            
            const price = parseFloat(element.content.options[optionIndex].price);
            if (filterInNoVincs(userIds[k])) {
              summary[optionIndex].noVincs += count;
            } else {
              summary[optionIndex].vincs += count;
            }
            summary[optionIndex].total += count;
            summary[optionIndex].totalPrice += count * price;
            summary[optionIndex].users.push({ userId: userIds[k], count, userIndex: k });
          }
        });
      }
    });
    return summary;
  };

  const fillAndGo = (text) => {
    setFilterText(text);
    document.getElementById('respostes-anchor').scrollIntoView({ behavior: 'smooth' });
  }

  const getShortName = (userId) =>
    !(userId in castellersInfo) ? null :
    getCastellerDisplayName(castellersInfo[userId]);

  const getShortNamesForOption = (element, optionIndex) => {
    return responses
      .map((response, index) => {
        const userId = userIds[index];
        const shortName = getShortName(userId) || guestMotes[index] || 'VISITANT'
        const question_response = response[element.id];
        return { userId, shortName, question_response };
      })
      .filter(({ question_response }) => {
        if (element.type === 'checkbox') {
          return question_response && question_response[optionIndex];
        } else if (element.type === 'multiple-choice' || element.type === 'ticket') {
          return question_response === optionIndex;
        } else {
          return false;
        }
      })
      .sort((a, b) => a.shortName.localeCompare(b.shortName))
  };    

  const [uncollapsedComponents, setUncollapsedComponents] = useState([]);
  const [clickedOptions, setClickedOptions] = useState([]);
  const [visibleCount, setVisibleCount] = useState({});

  const toggleShortNamesVisibility = (elementId, optionIndex) => {
    const optionKey = `${elementId}-${optionIndex}`;
    if (clickedOptions.includes(optionKey)) {
      setClickedOptions(clickedOptions.filter((key) => key !== optionKey));
    } else {
      setClickedOptions([...clickedOptions, optionKey]);
    }
  };  

  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text, id) => {
    async function copyToClipboardInternal(text) {
      if (navigator.clipboard) {
        // Use Clipboard API
        await navigator.clipboard.writeText(text)
      } else if (document.queryCommandSupported("copy")) {
        // Fallback to older `execCommand` method
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          console.log("Text copied to clipboard.");
        } catch (err) {
          console.error("Error copying text to clipboard: ", err);
        } finally {
          document.body.removeChild(textarea);
        }
      } else {
        console.error("Copy to clipboard is not supported in your browser.");
      }
    }
    
    try {
      await copyToClipboardInternal(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copiableText = (element) => {
    if (element.type === 'short-answer') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        responses
          .map((response, index) => [userIds[index], guestMotes[index], response[element.id]])
          .filter(([userId, mote, response]) => response)
          .map(([userId, mote, response]) => `\t${getShortName(userId) || mote || 'VISITANT'}: "${response.trim()}"`)
          .join('\n\n')
      )
    } else if (element.type === 'paragraph') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        responses
          .map((response, index) => [userIds[index], response[element.id]])
          .filter(([userId, mote, response]) => response)
          .map(([userId, mote, response]) => `\t${getShortName(userId) || mote || 'VISITANT'}: "${response.trim().replace(/\n/g, ' ')}"`)
          .join('\n\n')
      )
    } else if (element.type === 'multiple-choice' || element.type === 'ticket') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        element.content.options
          .map((option, index) => [option, getShortNamesForOption(element, index).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).map(({ shortName }) => shortName)])
          .filter(([option, shortNames]) => shortNames.length > 0)
          .filter(([option, shortNames]) => !option.hidden)
          .map(([option, shortNames]) => `\t${option} (${shortNames.length})`)
          .join('\n')
      )
    } else if (element.type === 'checkbox') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        element.content.options
          .map((option, index) => [option, getShortNamesForOption(element, index).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).map(({ shortName }) => shortName)])
          .filter(([option, shortNames]) => shortNames.length > 0)
          .filter(([option, shortNames]) => !option.hidden)
          .map(([option, shortNames]) => `\t${option} (${shortNames.length})`)
          .join('\n')
      )
    } else if (element.type === 'ticket-v2') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        element.content.options
          .map((option, index) => {
            if (option.hidden) return null;
            const summary = summarizeTicketV2(element);
            if (!summary || summary.length === 0 || !summary[index]) return null;
            return `\t${option.text} (${formatMoney(option.price)}€) - ${summary[index].total}`;
          })
          .filter(line => line !== null)
          .join('\n')
      );
    } else if (element.type === 'ticket-v2-single') {
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        element.content.options
          .map((option, index) => {
            if (option.hidden) return null;
            const summary = summarizeTicketV2(element);
            if (!summary || summary.length === 0 || !summary[index]) return null;
            return `\t${option.text} (${formatMoney(option.price)}€) - ${summary[index].total}`;
          })
          .filter(line => line !== null)
          .join('\n')
      );
    } else if (element.type === 'assistance') {
      const yesLabel = element.content?.yesLabel || 'Sí';
      const noLabel = element.content?.noLabel || 'No';
      const yesCount = responses.filter(r => r[element.id] === 1).filter((_, i) => showNoVincs || filterOutNoVincs(userIds[i])).length;
      const noCount = responses.filter(r => r[element.id] === 0).filter((_, i) => showNoVincs || filterOutNoVincs(userIds[i])).length;
      return (
        '*' +
        getQuestionLabel(element) +
        '*' +
        '\n\n' +
        `\t${yesLabel} (${yesCount})\n` +
        `\t${noLabel} (${noCount})`
      );
    } else {
      return '';
    }
  }

  const copyAllResponses = () => {
    // First get all the text
    const title_text = '*' + form.title.toUpperCase() + '*';

    const elements_text = form.elements
      .filter((element) => copiableText(element) !== '')
      .map((element) => copiableText(element))
      .join('\n\n');
      
    const full_text = title_text
      + (form.elements.length > 0 ? '\n\n' : '\n\n')
      + elements_text;

    // Then copy it
    copyToClipboard(full_text)
  }

  const excludePeopleThatDontPay = (response, element) => {
    const userDoesntPay = totalPaidPerResponse(response) === 0;
    const elementIsComprovant = element?.isComprovant === true;
    const skipWhenNoPay = element?.skipWhenNoPay !== false; // Default to true

    if (elementIsComprovant && skipWhenNoPay && userDoesntPay) return false;
    // Nor whoever was never asked for it: a transfer receipt is not for someone who
    // paid in cash or by card
    return isFormElementVisible(element, form, response);
  }

  const [formImageModalIsClosed, setFormImageModalIsClosed] = useState(true);
  const [focusedResponseInfo, setFocusedResponseInfo] = useState(null);

  useEffect(() => {
    if (formImageModalIsClosed) {
      setFocusedResponseInfo(null);
    }
  }, [
    formImageModalIsClosed,
  ])

  const statusOfTickets = (response, user) => {
    const tickets = form.elements
      .filter(element => element.type === 'ticket' || element.type === 'ticket-v2' || element.type === 'ticket-v2-single')
      .filter(element => response?.[element.id] === 0 || Object.values(response?.[element.id] || {})?.some(quantity => quantity > 0))
      .map(ticket => statusPagaments?.[ticket.id]?.find(status => samePerson(status['casteller-id'], user))?.status)

    return tickets
  }

  const getOtherQuestionsArray = (element) => responses
    .map((response, index) => [response, index])
    .sort((a, b) => {
      const aStatus = statusOfTickets(a[0], userIds[a[1]]);
      const bStatus = statusOfTickets(b[0], userIds[b[1]]);

      if (aStatus.includes('Pagat') && bStatus.includes('Pagat')) {
        return 0;
      } else if (aStatus.includes('Pagat')) {
        return 1;
      } else if (bStatus.includes('Pagat')) {
        return -1;
      }
    })
    .map(([response, index]) => ({
      name: getShortName(userIds[index]) || guestMotes[index] || 'VISITANT',
      user: userIds[index],
      response,
      element,
    }))
    .filter(({ response }) => excludePeopleThatDontPay(response, element))

  const totalMoney = useMemo(() => {
    if (!form) return 0;
    
    // 1) New ticket-v2 flavours
    const v2Money = form.elements
      .filter(el => el.type === 'ticket-v2' || el.type === 'ticket-v2-single')
      .reduce((total, el) => {
        const summary = summarizeTicketV2(el);
        if (!summary || summary.length === 0) return total;
        const elTotal = summary.reduce((sum, option) => sum + (option?.totalPrice || 0), 0);
        return total + elTotal;
      }, 0);

    // 2) Legacy yes/no `ticket` elements
    const legacyMoney = form.elements
      .filter(el => el.type === 'ticket')
      .reduce((total, el) => {
        // Count how many users selected the first option (index 0) which means they pay
        const countYes = responses.reduce((sum, response) => sum + (response?.[el.id] === 0 ? 1 : 0), 0);
        return total + countYes * (parseFloat(el.price) || 0);
      }, 0);

    return v2Money + legacyMoney;
  }, [form, responses, summarizeTicketV2]);

  // Calculate stripe commission (only for transactions with colla_absorbs mode)
  const stripeCommissionData = useMemo(() => {
    const stripeElement = form?.elements?.find(el => el.type === 'stripe-payment');
    if (!stripeElement) return { commission: 0, transactionCount: 0, collaAbsorbsCount: 0 };
    
    const { percent: COMMISSION_PERCENT, fixed: COMMISSION_FIXED } = getStripeCommissionInfoSync();
    // Get form's global commission mode for fallback (old payments without stored commissionMode)
    const formCommissionMode = stripeElement.content?.commissionMode || 'user_pays';
    
    let totalCommission = 0;
    let transactionCount = 0;
    let collaAbsorbsCount = 0;
    
    Object.entries(stripePayments || {}).forEach(([userId, paymentData]) => {
      const payments = paymentData?.payments || [];
      payments.forEach(payment => {
        const amount = payment?.amount || 0;
        if (amount > 0) {
          transactionCount++;
          // Use actual commission mode from payment if available,
          // otherwise fall back to form's global setting (for old payments)
          const paymentCommissionMode = payment?.commissionMode ?? formCommissionMode;
          if (paymentCommissionMode === 'colla_absorbs') {
            collaAbsorbsCount++;
            // Use stored commission if available, otherwise calculate from baseAmount
            const commission = payment?.commission != null
              ? payment.commission
              : (payment?.baseAmount || amount) * COMMISSION_PERCENT + COMMISSION_FIXED;
            totalCommission += commission;
          }
          // If commissionMode is 'user_pays', the colla doesn't pay the commission
        }
      });
    });
    
    return {
      commission: Math.round(totalCommission * 100) / 100,
      transactionCount,
      collaAbsorbsCount
    };
  }, [form, stripePayments]);

  return (<>
    <PopupFormImage
      modalIsClosed={formImageModalIsClosed}
      setModalIsClosed={setFormImageModalIsClosed}
      evId={selectedEvent}
      form={form}
      statusPagaments={statusPagaments}
      setStatusPagaments={setStatusPagaments}
      socket={socket}

      name={focusedResponseInfo?.name}
      user={focusedResponseInfo?.user}
      element={focusedResponseInfo?.element}
      response={focusedResponseInfo?.response}

      getOtherQuestionsArray={getOtherQuestionsArray}
      setFocusedResponseInfo={setFocusedResponseInfo}
      recognize={recognize}
    />

    <div className={'responses-container'} style={{ fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ flex: 2 }}>Recompte de les respostes</h2>
        <label
          style={{
            flex: 1,
            fontSize: 12,
            textAlign: 'right',
          }}
        >
          <input
            type="checkbox"
            checked={showNoVincs}
            onChange={() => setShowNoVincs(prev => !prev)}
            style={{
              width: 10,
              height: 10,
            }}
          />
          Mostra les respostes de gent que no ve
        </label>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <h4>
          Total: {userIds.filter(filterOutNoVincs).length > 0 && userIds.filter(filterOutNoVincs).length}
          { showNoVincs && userIds.filter(filterOutNoVincs).length > 0 && userIds.filter(filterInNoVincs).length > 0 && ' + ' }
          <span style={{ color: 'rgb(255, 80, 80)' }}>{ showNoVincs && userIds.filter(filterInNoVincs).length > 0 && userIds.filter(filterInNoVincs).length }</span>
          &nbsp;respost{userIds.length === 1 ? 'a' : 'es'}
        </h4>

        <div style={{ alignSelf:'center' }}>
          <button onClick={copyAllResponses} style={{ backgroundColor: '#333' }}>
            {isCopied ? 'Copiat!' : 'Copiar respostes'}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {
          userIds
            .filter((_, index) => uncollapsedComponents.includes('users') ? true : index < 5)
            .map((userId, index) => {
              return (
                <div
                  key={userId}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 5,
                    border: '1px solid #ccc',
                    borderRadius: 5,
                    margin: 5,
                    fontSize: 12,
                    backgroundColor:
                      assistenciesEvent
                        ?.find(assist => parseInt(assist.user) === parseInt(userId))
                        ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' :
                      'transparent',
                    display:
                      !showNoVincs && assistenciesEvent
                        ?.find(assist => parseInt(assist.user) === parseInt(userId))
                        ?.assistencia === 'No vinc' ? 'none' : 'flex'
                  }}
                >
                  <a
                    href="#respostes-anchor"
                    onClick={() => setFilterText(getShortName(userId) || guestMotes[index] || 'VISITANT')}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'black'
                    }}
                  >
                    {getShortName(userId) || guestMotes[index] || 'VISITANT'}
                  </a>
                  {guestDuplicates.has(index) ? <span style={{ color: '#999' }}>possible duplicat</span> : null}
                </div>
              )
            })
        }

        { userIds.length > 5 && <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 5,
            border: '1px solid #ccc',
            borderRadius: 5,
            margin: 5,
            fontSize: 12
          }}
          onClick={() => setUncollapsedComponents(uncollapsedComponents.includes('users') ? uncollapsedComponents.filter((component) => component !== 'users') : [...uncollapsedComponents, 'users'])}
        >
          <span style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            {uncollapsedComponents.includes('users') ? 'Mostra menys' : `+${userIds.length - 5} més`}
          </span>
        </div> }
      </div>

      {form.order
      .map((elementId) => form.elements.find((el) => el.id === elementId))
      .filter(element => element)
      .filter(element => isPublic ? !element.isComprovant : true)
      .filter(element => hasAnyResponse(element, responses))
      .map((element) => {
        if (element.type === 'multiple-choice' || element.type === 'ticket') {
          const summary = summarizeMultipleChoice(element);
          // Skip rendering if summary is empty (invalid element structure)
          if (!summary || summary.length === 0) {
            return null;
          }
          const maxOfSummary = summary.map(s => s.vincs).reduce((a, b) => b > a ? b : a, 0);
          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {element.content.options.map((option, index) => (
                  <div
                    key={`${element.id}-option-${index}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div
                      onClick={() => toggleShortNamesVisibility(element.id, index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ maxWidth: '75%' }}>
                        <input type="radio" checked={summary[index] && summary[index].vincs === maxOfSummary && maxOfSummary > 0} disabled={true} />
                        <a style={{ color: 'black' }} id={`#${element.id}-${option}-${index}`} href={`#${element.id}-${option}-${index}`}>
                          {option}
                        </a>
                        { element.type === 'ticket' && index === 0 ? <> ({formatMoney(element.price)}€)</> : <></> }
                      </span>
                      <hr
                        style={{
                          flexGrow: 1,
                          borderTop: '1px solid #ccc',
                          borderColor: '#ccc',
                          marginLeft: '0.5rem',
                          marginRight: '0.5rem',
                          alignSelf: 'center',
                        }}
                      />
                      <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: !summary[index] || summary[index].vincs === 0 ? '#ccc' : 'black' }}>{summary[index]?.vincs || 0}</span>
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> }
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{summary[index].noVincs}</span> }
                      { element.limit && index === 0 && <span style={{ color: 'black' }}>{`/${element.limit}`}</span> }
                    </div>

                    <div
                      style={{
                        display: getShortNamesForOption(element, index).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).length > 0 && clickedOptions.includes(`${element.id}-${index}`) ? 'flex' : 'none',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginLeft: '2rem',
                      }}
                    >
                      {
                        getShortNamesForOption(element, index).map(({ userId, shortName }, index) => (
                          <a
                            key={userId}
                            href="#respostes-anchor"
                            onClick={() => {
                              setFilterText('');
                              setResponseIdx(userIds.indexOf(userId));
                            }}
                            style={{
                              margin: 5,
                              padding: 5,
                              borderStyle: 'solid',
                              borderWidth: 1,
                              borderColor: '#ccc',
                              borderRadius: 5,
                              fontSize: 12,
                              textDecoration: 'none',
                              color: 'black',
                              backgroundColor: assistenciesEvent
                                ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                              display:
                                !showNoVincs && assistenciesEvent
                                  ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                  ?.assistencia === 'No vinc' ? 'none' : 'flex'
                            }}
                          >
                            {shortName}
                          </a>
                        ))
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        } else if (element.type === 'checkbox') {
          const summary = summarizeCheckbox(element);
          // Skip rendering if summary is empty (invalid element structure)
          if (!summary || summary.length === 0) {
            return null;
          }
          const maxOfSummary = summary.reduce((a, b) => b > a ? b : a, 0);
          return (
            <div key={element.id} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {element.content.options.map((option, index) => (
                  <div
                    key={`${element.id}-option-${index}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                    }}
                  >
                  <div
                    onClick={() => toggleShortNamesVisibility(element.id, index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      position: 'relative',
                    }}
                  >
                    <span style={{ maxWidth: '75%' }}>
                      <input type="checkbox" checked={summary[index] && summary[index].vincs === maxOfSummary && maxOfSummary > 0} disabled={true} />
                      <a style={{ color: 'black' }} id={`#${element.id}-${option}-${index}`} href={`#${element.id}-${option}-${index}`}>
                        {option}
                      </a>
                    </span>
                    <hr
                      style={{
                        flexGrow: 1,
                        borderTop: '1px solid #ccc',
                        borderColor: '#ccc',
                        marginLeft: '0.5rem',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                      }}
                    />
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: !summary[index] || summary[index].vincs === 0 ? '#ccc' : 'black' }}>{summary[index]?.vincs || 0}</span>
                    { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> }
                    { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{summary[index].noVincs}</span> }
                  </div>

                  <div
                    style={{
                      display: getShortNamesForOption(element, index).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).length > 0 && clickedOptions.includes(`${element.id}-${index}`) ? 'flex' : 'none',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginLeft: '2rem',
                    }}
                  >
                  {
                    getShortNamesForOption(element, index).map(({ shortName, userId }, index) => (
                      <a
                        key={userId}
                        href="#respostes-anchor"
                        onClick={() => {
                          setFilterText('');
                          setResponseIdx(userIds.indexOf(userId));
                        }}
                        style={{
                          margin: 5,
                          padding: 5,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: '#ccc',
                          borderRadius: 5,
                          fontSize: 12,
                          textDecoration: 'none',
                          color: 'black',
                          backgroundColor: assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                          display: 
                            !showNoVincs && assistenciesEvent
                              ?.find(assist => parseInt(assist.user) === parseInt(userId))
                              ?.assistencia === 'No vinc' ? 'none' : 'flex'
                        }}
                      >
                        {shortName}
                      </a>
                    ))
                  }
                  </div>
                </div>
                ))}
              </div>
            </div>
          );
        } else if (element.type === 'short-answer' || element.type === 'paragraph') {
          return (
            <div key={element.id} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Regular responses */}
                {responses
                  .map((response, index) => [userIds[index], response[element.id], index])
                  .filter(([userId, response]) => response && !isResponseSpam(response))
                  .filter(([userId, response], index) => !uncollapsedComponents.includes(element.id) && index < 3 || uncollapsedComponents.includes(element.id))
                  .sort((a, b) => {
                    const displayNameA = getShortName(a[0]) || guestMotes[a[2]] || 'VISITANT';
                    const displayNameB = getShortName(b[0]) || guestMotes[b[2]] || 'VISITANT';
                    return displayNameA.localeCompare(displayNameB);
                  })
                  .map(([userId, response, response_index], index) => (
                    <div
                      key={userId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem',
                        position: 'relative',
                        color: assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'rgb(255, 100, 100)' : 'black',
                        display: 
                          !showNoVincs && assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'none' : 'flex'
                      }}
                    >
                      <span style={{ maxWidth: '75%' }}>
                        <strong>{getShortName(userId) || guestMotes[response_index] || 'VISITANT'}:</strong> <em>"{response.trim()}"</em>
                      </span>
                    </div>
                  ))
                }

                {/* Separator between regular and spam responses */}
                {responses.some(r => r[element.id] && isResponseSpam(r[element.id])) && 
                  responses.some(r => r[element.id] && !isResponseSpam(r[element.id])) && 
                  uncollapsedComponents.includes(element.id) && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0 0.5rem'
                    }}>
                      <div style={{ 
                        borderTop: '1px solid #ddd',
                        margin: '1rem 0 1.5rem 0',
                        flex: 1
                      }} />
                      <div 
                        onClick={() => alert(`Hi ha dos tipus de paraules considerades spam:\n\n1. Paraules que si apareixen dins la resposta, són considerades spam: ${includeSpamWords.join(', ')}\n\n2. Respostes redundants (si la resposta és exactament aquesta paraula): ${exactSpamWords.join(', ')}`)}
                        style={{ 
                          cursor: 'pointer',
                          color: '#666',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          minWidth: '24px',
                          minHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #666',
                          borderRadius: '50%',
                          padding: '0.25rem',
                          touchAction: 'manipulation'
                        }}
                      >
                        ?
                      </div>
                    </div>
                )}

                {/* Spam responses */}
                {responses
                  .map((response, index) => [userIds[index], response[element.id], index])
                  .filter(([userId, response]) => response && isResponseSpam(response))
                  .filter(([userId, response], index) => {
                    const nonSpamCount = responses.filter(r => r[element.id] && !isResponseSpam(r[element.id])).length;
                    if (nonSpamCount > 3 && !uncollapsedComponents.includes(element.id)) return false;
                    return !uncollapsedComponents.includes(element.id) && index < 3 - nonSpamCount || uncollapsedComponents.includes(element.id);
                  })
                  .map(([userId, response, response_index], index) => (
                    <div
                      key={userId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem',
                        position: 'relative',
                        color: '#999',
                        fontStyle: 'italic'
                      }}
                    >
                      <span style={{ maxWidth: '75%' }}>
                        <strong>{getShortName(userId) || guestMotes[response_index] || 'VISITANT'}:</strong> <em>"{response.trim()}"</em>
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8em' }}>(marcat com spam)</span>
                      </span>
                    </div>
                  ))
                }

                { responses.filter(r => r[element.id]).length > 3 && <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
                  <button
                    onClick={() => {
                      if (uncollapsedComponents.includes(element.id)) {
                        setUncollapsedComponents(uncollapsedComponents.filter((id) => id !== element.id));
                      } else {
                        setUncollapsedComponents([...uncollapsedComponents, element.id]);
                      }
                    }}
                  >
                    {uncollapsedComponents.includes(element.id) ? "Mostra'n només 3" : `Mostra totes les ${responses.filter(r => r[element.id]).length} respostes`}
                  </button>
                </div> }
              </div>
            </div>
          );
        } else if (element.type === 'image-upload') {
          return (
            <div key={element.id}>
              <h3>{getQuestionLabel(element)}</h3>
              {
                element?.isComprovant === true && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 20, }}>
                    <p style={{ fontSize: 12, marginBottom: 20, fontStyle: 'italic' }}>
                      Pots marcar com a "pagats" els pagaments de cada persona fent click als botons.
                    </p>
                    <div
                      style={{
                        flexShrink: 0,
                      }}
                    >
                      <button
                        onClick={() => setShowRecogintion(prev => !prev)}
                      >
                        {
                          !showRecogintion ? '🪄 Llegeix' :
                          '👁️ Mostra'
                        }
                      </button>
                    </div>
                  </div>
                )
              }
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                {
                  responses
                    .map((response, index) => [response, index])
                    // .sort((a, b) => {
                    //   const aStatus = statusOfTickets(a[0], userIds[a[1]]);
                    //   const bStatus = statusOfTickets(b[0], userIds[b[1]]);

                    //   if (aStatus.includes('Pagat') && bStatus.includes('Pagat')) {
                    //     return 0;
                    //   } else if (aStatus.includes('Pagat')) {
                    //     return 1;
                    //   } else if (bStatus.includes('Pagat')) {
                    //     return -1;
                    //   }
                    // })
                    .reverse()
                    .map(([response, index]) => excludePeopleThatDontPay(response, element) && (
                      <div key={userIds[index]} style={{ margin: 5 }}>
                        <FormImagePreview
                          id={element.id}
                          evId={selectedEvent}
                          user={userIds[index]}
                          name={getShortName(userIds[index]) || guestMotes[index] || 'VISITANT'}
                          recognize={recognize}
                          showRecogintion={showRecogintion}
                          setModalIsClosed={setFormImageModalIsClosed}

                          noVe={
                            assistenciesEvent
                              ?.find(assist => parseInt(assist.user) === parseInt(userIds[index]))
                              ?.assistencia === 'No vinc'
                          }
                          showNoVincs={showNoVincs}

                          setFocusedResponseInfo={setFocusedResponseInfo}

                          response={response}
                          form={form}
                          element={element}
                          statusPagaments={statusPagaments}
                          setStatusPagaments={setStatusPagaments}
                          socket={socket}
                          socialMediaData={socialMediaData}
                        />
                      </div>
                    ))
                    .filter((element) => element)
                    .slice(0, (visibleCount[element.id] || 10))
                }
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    {(visibleCount[element.id] || 5) < responses.filter(response => excludePeopleThatDontPay(response, element)).length && (<>
                      <button onClick={() => setVisibleCount(prev => ({ ...prev, [element.id]: (prev[element.id] || 10) + 10 }))}>
                        + Mostra'n més
                      </button>
                      <button onClick={() => setVisibleCount(prev => ({ ...prev, [element.id]: responses.length }))}>
                        + Mostra-les totes
                      </button>
                    </>)}
                    {(visibleCount[element.id] || 5) > 5 && (
                      <button onClick={() => setVisibleCount(prev => ({ ...prev, [element.id]: 10 }))}>
                        - Mostra'n menys
                      </button>
                    )}
                  </div>
              </div>
            </div>
          )
        } else if (element.type === 'ticket-v2') {
          const summary = summarizeTicketV2(element);
          // Skip rendering if summary is empty (invalid element structure)
          if (!summary || summary.length === 0) {
            return null;
          }
          const maxOfSummary = summary.map(s => s.total).reduce((a, b) => b > a ? b : a, 0);
          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {element.content.options.map((option, index) => !option.hidden && (
                  <div
                    key={`${element.id}-option-${index}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div
                      onClick={() => toggleShortNamesVisibility(element.id, index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ maxWidth: '75%' }}>
                        <input type="radio" checked={summary[index] && summary[index].total === maxOfSummary && maxOfSummary > 0} disabled={true} />
                        <a
                          style={{
                            color: 'black',
                          }}
                          id={`#${element.id}-${option.text}-${index}`}
                          href={`#${element.id}-${option.text}-${index}`}
                        >
                          {option.text} ({formatMoney(option.price)}€)
                        </a>
                      </span>
                      <hr
                        style={{
                          flexGrow: 1,
                          borderTop: '1px solid #ccc',
                          borderColor: '#ccc',
                          marginLeft: '0.5rem',
                          marginRight: '0.5rem',
                          alignSelf: 'center',
                        }}
                      />
                      <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: !summary[index] || summary[index].vincs === 0 ? '#ccc' : 'black' }}>
                        {summary[index]?.vincs || 0}
                      </span>
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> }
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{summary[index].noVincs}</span> }
                      { option.limit && <span style={{ color: 'black' }}>{`/${option.limit}`}</span> }
                    </div>

                    <div
                      style={{
                        display: summary[index]?.users?.length > 0 && clickedOptions.includes(`${element.id}-${index}`) ? 'flex' : 'none',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginLeft: '2rem',
                      }}
                    >
                      {summary[index].users
                      .sort((a, b) => {
                        const displayNameA = getShortName(a.userId) || guestMotes[a.userIndex] || 'VISITANT';
                        const displayNameB = getShortName(b.userId) || guestMotes[b.userIndex] || 'VISITANT';
                        return displayNameA.localeCompare(displayNameB);
                      })
                      .map(({ userId, count, userIndex }) => {
                        const shortName = getShortName(userId) || guestMotes[userIndex] || 'VISITANT';
                        return (
                          <a
                            key={`${userId}-${userIndex}`}
                            href="#respostes-anchor"
                            onClick={() => {
                              setFilterText('');
                              setResponseIdx(userIndex);
                            }}
                            style={{
                              margin: 5,
                              padding: 5,
                              borderStyle: 'solid',
                              borderWidth: 1,
                              borderColor: '#ccc',
                              borderRadius: 5,
                              fontSize: 12,
                              textDecoration: 'none',
                              color: 'black',
                              backgroundColor: assistenciesEvent
                                ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                              display:
                                !showNoVincs && assistenciesEvent
                                  ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                  ?.assistencia === 'No vinc' ? 'none' : 'flex'
                            }}
                          >
                            {shortName} { count > 1 && `(${count})` }
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        } else if (element.type === 'ticket-v2-single') {
          const summary = summarizeTicketV2(element);
          // Skip rendering if summary is empty (invalid element structure)
          if (!summary || summary.length === 0) {
            return null;
          }
          const maxOfSummary = summary.map(s => s.total).reduce((a, b) => b > a ? b : a, 0);
          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {element.content.options.map((option, index) => !option.hidden && (
                  <div
                    key={`${element.id}-option-${index}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <div
                      onClick={() => toggleShortNamesVisibility(element.id, index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ maxWidth: '75%' }}>
                        <input type="radio" checked={summary[index] && summary[index].total === maxOfSummary && maxOfSummary > 0} disabled={true} />
                        <a
                          style={{
                            color: 'black',
                          }}
                          id={`#${element.id}-${option.text}-${index}`}
                          href={`#${element.id}-${option.text}-${index}`}
                        >
                          {option.text} ({formatMoney(option.price)}€)
                        </a>
                      </span>
                      <hr
                        style={{
                          flexGrow: 1,
                          borderTop: '1px solid #ccc',
                          borderColor: '#ccc',
                          marginLeft: '0.5rem',
                          marginRight: '0.5rem',
                          alignSelf: 'center',
                        }}
                      />
                      <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: !summary[index] || summary[index].vincs === 0 ? '#ccc' : 'black' }}>
                        {summary[index]?.vincs || 0}
                      </span>
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> }
                      { showNoVincs && summary[index] && summary[index].noVincs > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{summary[index].noVincs}</span> }
                      { option.limit && <span style={{ color: 'black' }}>{`/${option.limit}`}</span> }
                    </div>

                    <div
                      style={{
                        display: summary[index]?.users?.length > 0 && clickedOptions.includes(`${element.id}-${index}`) ? 'flex' : 'none',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginLeft: '2rem',
                      }}
                    >
                      {(summary[index]?.users || [])
                      .sort((a, b) => {
                        const displayNameA = getShortName(a.userId) || guestMotes[a.userIndex] || 'VISITANT';
                        const displayNameB = getShortName(b.userId) || guestMotes[b.userIndex] || 'VISITANT';
                        return displayNameA.localeCompare(displayNameB);
                      })
                      .map(({ userId, count, userIndex }) => {
                        const shortName = getShortName(userId) || guestMotes[userIndex] || 'VISITANT';
                        return (
                          <a
                            key={`${userId}-${userIndex}`}
                            href="#respostes-anchor"
                            onClick={() => {
                              setFilterText('');
                              setResponseIdx(userIndex);
                            }}
                            style={{
                              margin: 5,
                              padding: 5,
                              borderStyle: 'solid',
                              borderWidth: 1,
                              borderColor: '#ccc',
                              borderRadius: 5,
                              fontSize: 12,
                              textDecoration: 'none',
                              color: 'black',
                              backgroundColor: assistenciesEvent
                                ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                              display:
                                !showNoVincs && assistenciesEvent
                                  ?.find(assist => parseInt(assist.user) === parseInt(userId))
                                  ?.assistencia === 'No vinc' ? 'none' : 'flex'
                            }}
                          >
                            {shortName} { count > 1 && `(${count})` }
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        } else if (element.type === 'resum-pagaments') {
          const totalAfterCommission = Math.round((totalMoney - stripeCommissionData.commission) * 100) / 100;
          const paymentElements = form.elements
            .filter(el => el.type === 'ticket' || el.type === 'ticket-v2' || el.type === 'ticket-v2-single')
            .filter(el => hasAnyResponse(el, responses));
          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Resum de pagaments</h3>
                <h3 style={{ color: '#000' }}>{formatMoney(totalAfterCommission)}€</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {paymentElements.map((el, index) => {
                    let elTotal = 0;

                    if (el.type === 'ticket') {
                      const countYes = responses.reduce((sum, response) => sum + (response?.[el.id] === 0 ? 1 : 0), 0);
                      elTotal = countYes * (parseFloat(el.price) || 0);
                      return (
                        <div key={el.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>{getQuestionLabel(el)}</span>
                            <span style={{ fontWeight: 'bold' }}>{formatMoney(elTotal)}€</span>
                          </div>
                        </div>
                      );
                    } else {
                      const summary = summarizeTicketV2(el);
                      elTotal = summary.reduce((sum, option) => sum + option.totalPrice, 0);
                      return (
                        <div key={el.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>{getQuestionLabel(el)}</span>
                            <span style={{ fontWeight: 'bold' }}>{formatMoney(elTotal)}€</span>
                          </div>
                          {/* Show individual options */}
                          <div style={{ marginLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {el.content.options.map((option, optIndex) => {
                              if (option.hidden) return null;
                              const optionTotal = summary[optIndex]?.totalPrice || 0;
                              const quantity = summary[optIndex]?.total || 0;
                              if (quantity === 0) return null;
                              return (
                                <div key={optIndex} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.9em', color: '#666' }}>
                                    {option.text} ({quantity} × {formatMoney(option.price)}€)
                                  </span>
                                  <span style={{ color: '#666' }}>{formatMoney(optionTotal)}€</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                  })}
                {stripeCommissionData.commission > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>Comissions targeta</span>
                      <span style={{ fontWeight: 'bold' }}>-{formatMoney(stripeCommissionData.commission)}€</span>
                    </div>
                    <div style={{ marginLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9em', color: '#666' }}>
                          {stripeCommissionData.collaAbsorbsCount} {stripeCommissionData.collaAbsorbsCount === 1 ? 'transacció' : 'transaccions'} ({getStripeCommissionInfoSync().description})
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* Payment status summary */}
              <h3>Estat dels pagaments</h3>
              <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(() => {
                  // Calculate all amounts first to find the maximum
                  const statusAmounts = ['Entregat', 'Pagat', 'Demanat'].map(status => {
                    return paymentElements
                      .flatMap(el => {
                        if (el.type === 'ticket') {
                          const prodId = el.id;
                          const count = statusPagaments?.[prodId]?.filter(s => s.status === status)
                            .reduce((sum, s) => {
                              const userId = s['casteller-id'];
                              const userResponse = responses.find((_, i) => samePerson(userIds[i], userId));
                              return sum + ((userResponse?.[el.id] === 0) ? 1 : 0);
                            }, 0) || 0;
                          return [{ price: el.price, total: count }];
                        }
                        // ticket-v2 flavours
                        return el.content.options.map((option, optIndex) => {
                          if (option.hidden) return { price: 0, total: 0 };
                          const prodId = `${el.id}_${optIndex}`;
                          const count = statusPagaments?.[prodId]?.filter(s => s.status === status)
                            .reduce((sum, s) => {
                              const userId = s['casteller-id'];
                              const userResponse = responses.find((_, i) => samePerson(userIds[i], userId));
                              return sum + (userResponse?.[el.id]?.[optIndex] || 0);
                            }, 0) || 0;
                          return { price: option.price, total: count };
                        });
                      })
                      .reduce((sum, { price, total }) => sum + (price * total), 0);
                  });
                  
                  // Find the maximum amount for scaling
                  const maxAmount = Math.max(...statusAmounts, 1); // Avoid division by zero
                  
                  return ['Entregat', 'Pagat', 'Demanat'].map((status, index) => {
                    const colors = ['#6BA8D9', '#68C285', '#E69C55'];
                    const icons = ['📦', '✅', '🛒'];
                    const amount = statusAmounts[index];
                    const totalAmount = formatMoney(amount);

                    return (
                      <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                          <span>{icons[index]}</span>
                          <span>{status}</span>
                        </div>
                        <div style={{ flex: 4, backgroundColor: '#f0f0f0', borderRadius: '5px', overflow: 'hidden' }}>
                          <div style={{ width: `${(amount / maxAmount) * 100}%`, backgroundColor: colors[index], height: '10px' }}></div>
                        </div>
                        <span style={{ flex: 1, textAlign: 'right' }}>{totalAmount}€</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          );
        } else if (element.type === 'assistance') {
          const yesLabel = element.content?.yesLabel || 'Sí';
          const noLabel = element.content?.noLabel || 'No';
          
          // Count yes and no responses
          const yesCount = { vincs: 0, noVincs: 0 };
          const noCount = { vincs: 0, noVincs: 0 };
          
          responses.forEach((response, k) => {
            const answer = response[element.id];
            if (answer === 1) {
              if (filterInNoVincs(userIds[k])) {
                yesCount.noVincs++;
              } else {
                yesCount.vincs++;
              }
            } else if (answer === 0) {
              if (filterInNoVincs(userIds[k])) {
                noCount.noVincs++;
              } else {
                noCount.vincs++;
              }
            }
          });

          const getShortNamesForAssistance = (value) => {
            return responses
              .map((response, index) => {
                const userId = userIds[index];
                const shortName = getShortName(userId) || guestMotes[index] || 'VISITANT';
                const answer = response[element.id];
                return { userId, shortName, answer };
              })
              .filter(({ answer }) => answer === value)
              .sort((a, b) => a.shortName.localeCompare(b.shortName));
          };

          const maxCount = Math.max(yesCount.vincs, noCount.vincs);

          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>{getQuestionLabel(element)}</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Yes option */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div
                    onClick={() => toggleShortNamesVisibility(element.id, 'yes')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ maxWidth: '75%' }}>
                      <input type="radio" checked={yesCount.vincs === maxCount && maxCount > 0} disabled={true} />
                      {yesLabel}
                    </span>
                    <hr
                      style={{
                        flexGrow: 1,
                        borderTop: '1px solid #ccc',
                        borderColor: '#ccc',
                        marginLeft: '0.5rem',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                      }}
                    />
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: yesCount.vincs === 0 ? '#ccc' : 'black' }}>{yesCount.vincs}</span>
                    {showNoVincs && yesCount.noVincs > 0 ? <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> : null}
                    {showNoVincs && yesCount.noVincs > 0 ? <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{yesCount.noVincs}</span> : null}
                  </div>

                  <div
                    style={{
                      display: getShortNamesForAssistance(1).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).length > 0 && clickedOptions.includes(`${element.id}-yes`) ? 'flex' : 'none',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginLeft: '2rem',
                    }}
                  >
                    {getShortNamesForAssistance(1).map(({ userId, shortName }, index) => (
                      <a
                        key={userId}
                        href="#respostes-anchor"
                        onClick={() => {
                          setFilterText('');
                          setResponseIdx(userIds.indexOf(userId));
                        }}
                        style={{
                          margin: 5,
                          padding: 5,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: '#ccc',
                          borderRadius: 5,
                          fontSize: 12,
                          textDecoration: 'none',
                          color: 'black',
                          backgroundColor: assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                          display:
                            !showNoVincs && assistenciesEvent
                              ?.find(assist => parseInt(assist.user) === parseInt(userId))
                              ?.assistencia === 'No vinc' ? 'none' : 'flex'
                        }}
                      >
                        {shortName}
                      </a>
                    ))}
                  </div>
                </div>

                {/* No option */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div
                    onClick={() => toggleShortNamesVisibility(element.id, 'no')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ maxWidth: '75%' }}>
                      <input type="radio" checked={noCount.vincs === maxCount && maxCount > 0 && noCount.vincs > 0} disabled={true} />
                      {noLabel}
                    </span>
                    <hr
                      style={{
                        flexGrow: 1,
                        borderTop: '1px solid #ccc',
                        borderColor: '#ccc',
                        marginLeft: '0.5rem',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                      }}
                    />
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: noCount.vincs === 0 ? '#ccc' : 'black' }}>{noCount.vincs}</span>
                    {showNoVincs && noCount.noVincs > 0 ? <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> + </span> : null}
                    {showNoVincs && noCount.noVincs > 0 ? <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'rgb(255, 80, 80)' }}>{noCount.noVincs}</span> : null}
                  </div>

                  <div
                    style={{
                      display: getShortNamesForAssistance(0).filter(({ userId }) => showNoVincs || filterOutNoVincs(userId)).length > 0 && clickedOptions.includes(`${element.id}-no`) ? 'flex' : 'none',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginLeft: '2rem',
                    }}
                  >
                    {getShortNamesForAssistance(0).map(({ userId, shortName }, index) => (
                      <a
                        key={userId}
                        href="#respostes-anchor"
                        onClick={() => {
                          setFilterText('');
                          setResponseIdx(userIds.indexOf(userId));
                        }}
                        style={{
                          margin: 5,
                          padding: 5,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: '#ccc',
                          borderRadius: 5,
                          fontSize: 12,
                          textDecoration: 'none',
                          color: 'black',
                          backgroundColor: assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                          display:
                            !showNoVincs && assistenciesEvent
                              ?.find(assist => parseInt(assist.user) === parseInt(userId))
                              ?.assistencia === 'No vinc' ? 'none' : 'flex'
                        }}
                      >
                        {shortName}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        } else if (element.type === 'stripe-payment') {
          // Get list of users who paid with card
          const payersList = Object.entries(stripePayments || {})
            .filter(([userId, paymentData]) => (paymentData?.totalAmount || 0) > 0)
            .map(([userId, paymentData]) => ({
              userId,
              amount: paymentData?.totalAmount || 0,
              shortName: getShortName(userId) || guestMotes[userIds.indexOf(userId)] || 'VISITANT'
            }))
            .sort((a, b) => a.shortName.localeCompare(b.shortName));

          if (payersList.length === 0) return null;

          return (
            <div key={element.id} id={`form-element-${element.id}`} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <h3>Pagaments amb targeta</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div
                    onClick={() => toggleShortNamesVisibility(element.id, 'stripe')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ maxWidth: '75%', textDecoration: 'underline' }}>
                      💳 {payersList.length === 1 ? 'Persona que ha pagat amb targeta' : 'Persones que han pagat amb targeta'}
                    </span>
                    <hr
                      style={{
                        flexGrow: 1,
                        borderTop: '1px solid #ccc',
                        borderColor: '#ccc',
                        marginLeft: '0.5rem',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                      }}
                    />
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: 'black' }}>
                      {payersList.length}
                    </span>
                  </div>

                  <div
                    style={{
                      display: payersList.length > 0 && clickedOptions.includes(`${element.id}-stripe`) ? 'flex' : 'none',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginLeft: '2rem',
                    }}
                  >
                    {payersList.map(({ userId, amount, shortName }, index) => (
                      <a
                        key={userId}
                        href="#respostes-anchor"
                        onClick={() => {
                          setFilterText('');
                          setResponseIdx(userIds.indexOf(userId));
                        }}
                        style={{
                          margin: 5,
                          padding: 5,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: '#ccc',
                          borderRadius: 5,
                          fontSize: 12,
                          textDecoration: 'none',
                          color: 'black',
                          backgroundColor: assistenciesEvent
                            ?.find(assist => parseInt(assist.user) === parseInt(userId))
                            ?.assistencia === 'No vinc' ? 'rgb(255, 168, 168)' : 'transparent',
                          display:
                            !showNoVincs && assistenciesEvent
                              ?.find(assist => parseInt(assist.user) === parseInt(userId))
                              ?.assistencia === 'No vinc' ? 'none' : 'flex'
                        }}
                      >
                        {shortName}: {formatMoney(amount)}€
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        } else {
          return null;
        }
      })}
    </div>
  </>);  
};

// A web answer of someone outside the colla: the junta says which member it is, when
// it is one (backend/form-guests.js), and its lists then name the member
const GuestLink = ({ guestId, guest, typedName, castellersInfo, selectedEvent, actorId }) => {
  const [sending, setSending] = useState(false)
  if (!guestId || !String(guestId).startsWith('guest_')) return null
  const linked = guest?.casteller_id != null ? castellersInfo?.[guest.casteller_id] : null
  const candidates = linked ? [] : membersNamed(typedName, castellersInfo)
  if (!linked && candidates.length === 0) return null
  const link = (castellerId) => {
    setSending(true)
    postAPI(`/form_guests/${selectedEvent}/link`, { userId: actorId, guestId, castellerId },
      () => setSending(false), () => { setSending(false); alert("No s'ha pogut desar. Torna-ho a provar.") })
  }
  const name = (person) => [person?.nom, person?.cognom].filter(Boolean).join(' ') || person?.mote
  return (
    <div className="form-guest-link">
      {linked ? (
        <>
          <span>Respost des de la web per {name(linked)}</span>
          <button type="button" disabled={sending} onClick={() => link(null)}>Desfés</button>
        </>
      ) : candidates.map((candidate) => (
        <button key={candidate.id} type="button" disabled={sending} onClick={() => link(candidate.id)}>
          És {candidate.name}? ›
        </button>
      ))}
    </div>
  )
}

function FormResponses({ userInfo, socket, selectedEvent, castellersInfo, setCastellersInfo, isPublic, view }) {
    useSocketReconnect(socket, () => {
        if (!selectedEvent) return;
        socket.emit('.request_form_responses', selectedEvent);
        socket.emit('.request_google_sheet_url', selectedEvent);
    });

    const [form, setForm] = useState(null)
    const [responses, setResponses] = useState([])
    const [responsesLoaded, setResponsesLoaded] = useState(false)
    const [userIds, setUserIds] = useState([])
    const [assistenciesEvent, setAssistenciesEvent] = useState([])
    const [showRecogintion, setShowRecogintion] = useState(false)
    const [socialMediaData, setSocialMediaData] = useState({})
    const [stripePayments, setStripePayments] = useState({})
    const [stripeFormsEnabled, setStripeFormsEnabled] = useState(false)
    const [stripeWarningExpanded, setStripeWarningExpanded] = useState(false)

    const { ready, recognize } = useScheduler();

    // Who each web answer is: the name kept on the server, or the member the junta linked it to (formGuests.js)
    const formGuests = useFormGuests(selectedEvent, socket)
    const guestMotes = responses.map((response, index) => (castellersInfo?.[userIds[index]]
      ? response?.mote : guestLabel(userIds[index], response, formGuests, castellersInfo)))
    // Web answers named like another answer: maybe the same person twice
    const guestDuplicates = useMemo(
      () => possibleDuplicates({ userIds, responses, castellersInfo }),
      [userIds, responses, castellersInfo]
    )

    useEffect(() => {
      if (selectedEvent) fetchAPI(`/all_assistencies_event/${selectedEvent}`, data => setAssistenciesEvent(data.data))
    }, [
      selectedEvent,
    ])

    useEffect(() => {
      // Fetch social media data for all userIds
      userIds.forEach(userId => {
        if (userId && !socialMediaData[userId] && !isNaN(parseInt(userId))) {
          fetchAPI(`/get_social_media/${userId}`, (data) => {
            if (data && data.length > 0) {
              setSocialMediaData(prev => ({
                ...prev,
                [userId]: data[0]
              }))
            }
          })
        }
      })
    }, [userIds])

    // Check if Stripe Connect is configured, verified and enabled for forms
    useEffect(() => {
      fetchAPI('/stripe/connect/status', data => {
        if (data?.success) {
          setStripeFormsEnabled(data.configured && data.verified && data.formsEnabled);
        } else {
          setStripeFormsEnabled(false);
        }
      }, false);
    }, []);

    useEffect(() => {
      const handleLoadedForm = (form) => {
        if (!form.new) {
          setForm(form);
        } else {
          setForm(false)
        }
      }

      const handleFormResponses = (res) => {
        if (res.evId == selectedEvent) {
          const sortedEntries = Object.entries(res.responses).sort(([,a], [,b]) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateA < dateB ? -1 : 1
          });
          setResponses(sortedEntries.map(([,response]) => response));
          setUserIds(sortedEntries.map(([userId,]) => userId));
        }
        setResponsesLoaded(true);
      }

      const handleGoogleSheetUrl = ({ url }) => {
        setExcelUrl(url);
      }

      setResponsesLoaded(false);
      fetchAPI(`/load_form/${selectedEvent}`, handleLoadedForm)
      fetchAPI(`/form_responses/${selectedEvent}`, handleFormResponses)
      fetchAPI(`/google_sheet_url/${selectedEvent}`, handleGoogleSheetUrl)
    }, [selectedEvent])

    useEffect(() => {
      if (selectedEvent) {
        socket.emit('.request_google_sheet_url', selectedEvent);
      }
    }, [socket, selectedEvent])

    useEffect(() => {
        const onFormResponses = (res) => {
            if (res.evId == selectedEvent) {
              const sortedEntries = Object.entries(res.responses).sort(([,a], [,b]) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateA < dateB ? -1 : 1
              });
              setResponses(sortedEntries.map(([,response]) => response));
              setUserIds(sortedEntries.map(([userId,]) => userId));
              setResponsesLoaded(true);
            }
        }
        socket.on('.form_responses', onFormResponses)

        const onGoogleSheetUrlReceived = (sheetUrl) => {
          setExcelUrl(sheetUrl);
        };
        socket.on('.google_sheet_url_received', onGoogleSheetUrlReceived);

        const onNewResponse = () => {
          socket.emit('.request_form_responses', selectedEvent)
          socket.emit('.request_google_sheet_url', selectedEvent)
        }
        socket.on('.new_response', onNewResponse)

        return () => {
            socket.off('.form_responses', onFormResponses)
            socket.off('.google_sheet_url_received', onGoogleSheetUrlReceived)
            socket.off('.new_response', onNewResponse)
        }
    }, [selectedEvent])

    const [statusPagaments, setStatusPagaments] = useState({})

    useEffect(() => {
      const onPagamentStatusSet = data => {
        const prodId = data['producte-id'];
        const userId = data['casteller-id'];
        const status = data.status;

        setStatusPagaments(prev => ({
          ...prev,
          [prodId]: (prev[prodId] || [])
            .some(statusPagament => samePerson(statusPagament['casteller-id'], userId))
              ? (prev[prodId] || [])
                  .map(statusPagament => samePerson(statusPagament['casteller-id'], userId) ? { ...statusPagament, status } : statusPagament)
              : [...(prev[prodId] || []), { ...data, status }]
        }))
      }
      socket.on('.pagament_status_set', onPagamentStatusSet)

      return () => {
        socket.off('.pagament_status_set', onPagamentStatusSet)
      }
    }, [
      socket,
    ])

    useEffect(() => {
      const tickets = form
        ?.elements
        ?.filter(element => element.type === 'ticket')
        ?.map(element => element.id)
        ?.concat(
          form
            ?.elements
            ?.filter(element => element.type === 'ticket-v2' || element.type === 'ticket-v2-single')
            ?.map(element => element.content.options.map((option, index) => `${element.id}_${index}`))
            ?.flat()
        )
        ?.flat()

      const hasGuests = userIds.some(userId => String(userId).startsWith('guest_'))

      tickets
        ?.forEach(ticket_id => {
          // Each list replaces only its own rows, whichever answer arrives first.
          fetchAPI(`/pagaments/${ticket_id}`, (res) => {
            setStatusPagaments(prev => ({ ...prev, [ticket_id]: [...(Array.isArray(res) ? res : []), ...(prev[ticket_id] || []).filter(isGuestRow)] }))
          })
          if (hasGuests) fetchAPI(`/guest_pagaments/${ticket_id}`, (res) => {
            if (!Array.isArray(res)) return
            setStatusPagaments(prev => ({ ...prev, [ticket_id]: [...(prev[ticket_id] || []).filter(row => !isGuestRow(row)), ...res] }))
          })
        })
    }, [
      form,
      responses,
      userIds,
    ])

    // Fetch stripe payments when there's a stripe-payment element
    useEffect(() => {
      const hasStripePayment = form?.elements?.some(el => el.type === 'stripe-payment');
      if (hasStripePayment && selectedEvent) {
        fetchAPI(`/stripe/all-payments/${selectedEvent}`, (res) => {
          if (res?.success) {
            setStripePayments(res.payments || {});
          }
        });
      }
    }, [form, selectedEvent]);

    const renderResponse = (element, response, user) => {
        const { id, type } = element;
    
        switch (type) {
          case 'multiple-choice':
          case 'ticket':
            return <div>
              {
                element.content.options.map((option, index) => (
                  <div key={`${element.id}-option-${index}`}>
                    <input type="radio" checked={index === response} disabled={true} />
                    { index !== response ? <span style={{ color: '#bbb' }}>{option}</span> : <strong style={{ backgroundColor: filterText.length > 0 && queryInText(option, filterText) ? 'rgb(250, 200, 152)' : 'transparent' }}>{option}</strong> }
                  </div>
                ))
              }
            </div>;

          case 'ticket-v2':
            return (
              <div>
                {element.content.options.map((option, index) => {
                  if (option.hidden) return;
                  const ticketCount = response?.[index] ? response[index] : 0;
                  return (
                    <div key={`${element.id}-${option.text}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        backgroundColor: filterText.length > 0 && queryInText(option.text, filterText) ? 'rgb(250, 200, 152)' : 'transparent'
                      }}>
                        {option.text} ({formatMoney(option.price)}€)
                      </span>
                      <hr style={{ margin: '0 0.5rem', border: 'none', borderTop: '1px solid #ccc', flex: '1' }} />
                      {ticketCount > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> &times; {ticketCount}</span>}
                    </div>
                  );
                })}
              </div>
            );

          case 'ticket-v2-single':
            return (
              <div>
                {element.content.options.map((option, index) => {
                  if (option.hidden) return;
                  const ticketCount = response?.[index] ? response[index] : 0;
                  return (
                    <div key={`${element.id}-${option.text}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        backgroundColor: filterText.length > 0 && queryInText(option.text, filterText) ? 'rgb(250, 200, 152)' : 'transparent'
                      }}>
                        {option.text} ({formatMoney(option.price)}€)
                      </span>
                      <hr style={{ margin: '0 0.5rem', border: 'none', borderTop: '1px solid #ccc', flex: '1' }} />
                      {ticketCount > 0 && <span style={{ marginLeft: '0.2rem', fontWeight: 'bold', color: 'black' }}> &times; {ticketCount}</span>}
                    </div>
                  );
                })}
              </div>
            );

          case 'checkbox':
            return (
              <div>
                {
                    element.content.options
                        // .filter((_, index) => response[index])
                        .map((option, index) => (
                            <div key={`${element.id}-option-${index}`}>
                              <input type="checkbox" checked={response?.[index]} disabled={true} />
                              { response?.[index] ? <strong style={{ backgroundColor: filterText.length > 0 && queryInText(option, filterText) ? 'rgb(250, 200, 152)' : 'transparent' }}>{option}</strong> : <span style={{ color: '#bbb' }}>{option}</span> }
                            </div>
                        ))
                }
              </div>
            );
          case 'short-answer':
          case 'paragraph':
            if (!response) return <em>Sense resposta</em>;
            return <div style={{ border: "solid 1px black", borderRadius: 5, padding: 10, fontSize: 12, backgroundColor: 'rgb(255, 255, 230)' }}>
              <em style={{ backgroundColor: filterText.length > 0 && queryInText(response, filterText) ? 'rgb(250, 200, 152)' : 'transparent' }}>{response}</em>
            </div>;
          case 'image-upload':
            return <div>
              <FormImage
                id={id}
                evId={selectedEvent}
                user={user}
                width={200}
              />
            </div>
          case 'assistance':
            const yesLabel = element.content?.yesLabel || 'Sí';
            const noLabel = element.content?.noLabel || 'No';
            return (
              <div>
                <div>
                  <input type="radio" checked={response === 1} disabled={true} />
                  {response === 1 ? <strong>{yesLabel}</strong> : <span style={{ color: '#bbb' }}>{yesLabel}</span>}
                </div>
                <div>
                  <input type="radio" checked={response === 0} disabled={true} />
                  {response === 0 ? <strong>{noLabel}</strong> : <span style={{ color: '#bbb' }}>{noLabel}</span>}
                </div>
              </div>
            );
          default:
            return null;
        }
      };
    
      const renderResponseFull = (response, index) => {
        const confirmDeleteResponse = () => {
          const user = userIds[index];

          if (window.confirm(`Estàs segur que vols esborrar la resposta de ${response.fullName}?`)) {
            postAPI(
              `/delete_form_response`,
              { eventId: selectedEvent, userId: user, castellersInfo: castellersInfo },
              ({ error, ...res }) => {
                if (!error) {
                  // Success!
                  socket.emit('.new_response');
                }
              }
            );
          }
        }

        const assist = assistenciesEvent
          .find(assist => parseInt(assist.user) === parseInt(userIds[index]))
          ?.assistencia

        return (
          <div key={response.fullName} style={{ margin: 10, padding: 15, borderStyle: 'solid', borderWidth: 1, borderColor: '#ccc', borderRadius: 10, backgroundColor: 'white' }}>
            <h2>{response?.fullName}</h2>
            {form.order.map((elementId) => {
              const element = form.elements.find((el) => el.id === elementId);
              if (!element) return null;
              
              // Skip deleted elements with no response for this user
              if (element.deleted) {
                const value = response[elementId];
                const hasResponse = value !== undefined && value !== null && value !== '' && 
                  (typeof value !== 'object' || (Array.isArray(value) ? value.length > 0 : Object.values(value).some(v => v > 0)));
                if (!hasResponse) return null;
              }
              
              // Nothing to show for what was only on screen (an IBAN, a text) or never
              // asked to this person (the cash receipt of someone who paid by transfer)
              if (!element.deleted && !isFormElementVisible(element, form, response)) return null;
              // Aleta's payment page only shows to who has something to pay
              if (element.autoPayment && totalToPayOf(form, response) === 0) return null;
              const answer = renderResponse(element, response[elementId], response?.userId);
              if (answer === null) return null;

              return (
                <div key={elementId}>
                  <h3>{getQuestionLabel(element)}</h3>
                  {answer}
                </div>
              );
            })}

            {
              can(userInfo, 'DELETE_FORM_RESPONSE') &&
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 40 }}>
                  <button style={{ flex: 1, backgroundColor: 'rgb(255, 0, 0, 0.4)' }} onClick={confirmDeleteResponse}>Esborra resposta</button>
                </div>
            }
          </div>
        );
      }

      const handlePrev = () => {
        if (responseIdx > 0) {
          setResponseIdx(responseIdx - 1);
        }
      };
    
      const handleNext = () => {
        if (responseIdx < responses.length - 1) {
          setResponseIdx(responseIdx + 1);
        }
      };

      const handleFilterChange = (event) => {
        setFilterText(event.target.value);
      };

      const queryInText = (text, query) => {
        if (!text) return true
        const normalize = str => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]/g, "");
        return normalize(text).includes(normalize(query))
      }

      const getShortName = (userId) =>
        !(userId in castellersInfo) ? null :
        getCastellerDisplayName(castellersInfo[userId]);


      const getFilteredResponses = () => {
        return responses
          .map((response, index) => {
            const userId = userIds[index];
            const fullName = getFullName(userId) || guestMotes[index] || 'VISITANT';
            const shortName = getShortName(userId) || guestMotes[index] || 'VISITANT';
            return { ...response, ['userId']: userId, ['fullName']: fullName, ['shortName']: shortName };
          })
          .filter((response, index) => {
            return queryInText(response.fullName, filterText)
          })
      };    

      const [responseIdx, setResponseIdx] = useState(0)
      const [filterText, setFilterText] = useState('');

      const getFullName = (userId) => {
        if (!(userId in castellersInfo)) return null
        const { nom, cognom, mote } = castellersInfo[userId]
        if (mote) return `${nom} ${cognom} (${mote})`
        return `${nom} ${cognom}`
      }

      const [loadingExcel, setLoadingExcel] = useState(false)
      const [excelUrl, setExcelUrl] = useState(null)

      useEffect(() => {
        if (excelUrl) { 
          setLoadingExcel(false)
        }
      }, [excelUrl])

      const sendToExcel = () => {
        const writerEmails = ['andreuhuguet@gmail.com', 'aletacastellera@gmail.com']
        socket.emit('.send_to_excel', { responses, userIds, form, castellersInfo, writerEmails });
        setLoadingExcel(true)
      }

      const [emailList, setEmailList] = useState([])
      const [isCopied, setIsCopied] = useState(false)

      const copied = () => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
      }

      const externalFormShareEnabled = canShareExternalForm(COLLA, stripeFormsEnabled)

      return isPublic ? (
        <div>
            { 
              form && responses && responses.length > 0 && <>
                <Summary
                  assistenciesEvent={assistenciesEvent}
                  selectedEvent={selectedEvent}
                  guestMotes={guestMotes}
                  guestDuplicates={guestDuplicates}
                  responses={responses}
                  userIds={userIds}
                  form={form}
                  castellersInfo={castellersInfo}
                  setFilterText={setFilterText}
                  setResponseIdx={setResponseIdx}
                  statusPagaments={statusPagaments}
                  setStatusPagaments={setStatusPagaments}
                  socket={socket}
                  recognize={recognize}
                  showRecogintion={showRecogintion}
                  setShowRecogintion={setShowRecogintion}
                  isPublic={isPublic}
                  socialMediaData={socialMediaData}
                  stripePayments={stripePayments}
                />
              </>
            }
        </div>
      ) : !view || view === 'editar' || view === 'historial' ? null : (
        <div>
            { view === 'comparteix' && <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {form && (
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: 8, padding: 20, position: 'relative' }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>Formulari web per gent sense Aleta</h3>
                  <div style={{
                    filter: externalFormShareEnabled ? 'none' : 'blur(4px)',
                    pointerEvents: externalFormShareEnabled ? 'auto' : 'none',
                    userSelect: externalFormShareEnabled ? 'auto' : 'none'
                  }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#555' }}>
                      La gent que no tingui l'app instal·lada pot respondre aquest formulari mitjançant la web. Vigileu que no hi hagi duplicats entre l'app i la web.
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #ddd',
                      gap: 10,
                    }}>
                      <input
                        type="text"
                        value={`https://${COLLA}.tenimaleta.com/f/${selectedEvent}`}
                        readOnly
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, minWidth: 0 }}
                      />
                      <a
                        href={`/f/${selectedEvent}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ whiteSpace: 'nowrap', fontSize: 14 }}
                      >
                        Anar-hi
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://${COLLA}.tenimaleta.com/f/${selectedEvent}`)
                          copied()
                        }}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#77A9C5',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          fontSize: 14,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {isCopied ? 'Copiat!' : 'Copia'}
                      </button>
                    </div>
                  </div>
                  {!externalFormShareEnabled && (
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: 8
                    }}>
                      <div style={{
                        backgroundColor: '#FFF3CD',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid #FFECB5',
                        maxWidth: '90%',
                        textAlign: 'center'
                      }}>
                        <div
                          onClick={() => setStripeWarningExpanded(!stripeWarningExpanded)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            color: '#856404',
                            fontWeight: 'bold',
                            fontSize: 14
                          }}
                        >
                          <span>⚠️ Requereix <a href="/quotes/stripe" target="_blank" style={{ color: '#856404', fontWeight: 'bold' }}>pagaments amb targeta</a></span>
                          <span style={{
                            transform: stripeWarningExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>▼</span>
                        </div>
                        {stripeWarningExpanded && (
                          <p style={{ margin: '10px 0 0 0', color: '#856404', fontSize: 13 }}>
                            Per assegurar que els pagaments de fora de l'app estiguin pagats, cal tenir activats els <a href="/quotes/stripe" target="_blank" style={{ color: '#856404', fontWeight: 'bold' }}>pagaments amb targeta</a>.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ backgroundColor: '#f8f9fa', borderRadius: 8, padding: 20 }}>
                <h3 style={{ margin: '0 0 10px 0' }}>Resum de respostes en temps real</h3>
                <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#555' }}>
                  Pots compartir el resum de les respostes a temps real amb aquest enllaç:
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  padding: '8px 12px',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  gap: 10,
                }}>
                  <code style={{ flex: 1, wordBreak: 'break-all', fontSize: 14, minWidth: 0 }}>
                    {`https://${COLLA}.tenimaleta.com/forms/${selectedEvent}/respostes`}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`https://${COLLA}.tenimaleta.com/forms/${selectedEvent}/respostes`)
                      copied()
                    }}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#77A9C5',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 14,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isCopied ? 'Copiat!' : 'Copia'}
                  </button>
                </div>
              </div>

              <ShareExcel
                emailList={emailList}
                setEmailList={setEmailList}
                sendToExcel={sendToExcel}
                responses={responses}
                excelUrl={excelUrl}
                loadingExcel={loadingExcel}
                socket={socket}
                selectedEvent={selectedEvent}
                form={form}
              />
            </div> }

            { view === 'recompte' && form && responses && responses.length > 0 &&
              <Summary
                assistenciesEvent={assistenciesEvent}
                selectedEvent={selectedEvent}
                guestMotes={guestMotes}
                guestDuplicates={guestDuplicates}
                responses={responses}
                userIds={userIds}
                form={form}
                castellersInfo={castellersInfo}
                setFilterText={setFilterText}
                setResponseIdx={setResponseIdx}
                statusPagaments={statusPagaments}
                setStatusPagaments={setStatusPagaments}
                socket={socket}
                recognize={recognize}
                showRecogintion={showRecogintion}
                setShowRecogintion={setShowRecogintion}
                socialMediaData={socialMediaData}
                stripePayments={stripePayments}
              />
            }

            { view === 'recompte' && form && responsesLoaded && responses.length === 0 &&
              <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Encara no hi ha respostes</div>
            }

            { view === 'individuals' && form && responses && responses.length > 0 && <>

            <div id="respostes-anchor" style={{ display: 'flex', flexDirection: 'row' }}>
                <h2 style={{ flex: 1 }}>Respostes</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Filtrar per nom o resposta"
                value={filterText}
                onInput={handleFilterChange}
              />

              <button
                onClick={() => {
                  setFilterText('');
                }}
                style={{ marginLeft: '1rem' }}
              >
                Borra
              </button>
            </div>

            { filterText.length > 0 && <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
              { getFilteredResponses().length > 1 && <div style={{ flex: 1, textAlign: 'center' }}>
                  <h4>{getFilteredResponses().length} castellers trobats</h4>
                  <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                    {
                      getFilteredResponses().map((response, index) => {
                        return (
                          <a
                            key={response.userId}
                            href="#respostes-anchor"
                            onClick={() => {
                              setFilterText('');
                              setResponseIdx(userIds.indexOf(response.userId));
                            }}
                            style={{
                              margin: 5,
                              padding: 5,
                              borderStyle: 'solid',
                              borderWidth: 1,
                              borderColor: '#ccc',
                              borderRadius: 5,
                              fontSize: 12,
                              textDecoration: 'none',
                              color: 'black',
                            }}
                          >
                            {response.shortName}
                          </a>
                        )
                      })
                    }
                  </div>
              </div> }
              { getFilteredResponses().length === 0 && <div style={{ flex: 1, textAlign: 'center' }}>
                  <h4>No hi ha cap resposta</h4>
              </div> }
            </div> }

            { filterText.length === 0 && <div style={{ display: 'flex', flexDirection: 'row' }}>
                <h3 style={{ flex: 5, alignSelf: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getFullName(userIds[responseIdx]) || guestMotes[responseIdx] || 'VISITANT'}
                  {guestDuplicates.has(responseIdx) ? <span style={{ color: '#999', fontWeight: 'normal' }}> · possible duplicat</span> : null}
                </h3>

                <div style={{ flex: 1, textAlign: 'center', alignSelf: 'center' }}>
                  <h4>{responseIdx + 1}/{responses.length}</h4>
                </div>

                <div style={{ flex: 2, alignSelf: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button onClick={handlePrev} disabled={responseIdx === 0}>
                            &lt;
                        </button>
                        <button onClick={handleNext} disabled={responseIdx === responses.length - 1}>
                            &gt;
                        </button>
                    </div>
                </div>
            </div> }

            {filterText.length === 0 && !isPublic && !castellersInfo?.[userIds[responseIdx]] ? (
              <GuestLink
                guestId={userIds[responseIdx]}
                guest={formGuests[userIds[responseIdx]]}
                typedName={guestTypedName(userIds[responseIdx], responses[responseIdx], formGuests)}
                castellersInfo={castellersInfo}
                selectedEvent={selectedEvent}
                actorId={userInfo?.id}
              />
            ) : null}

            {filterText.length === 0 && getFilteredResponses().length > 1 && renderResponseFull(getFilteredResponses()[responseIdx], responseIdx)}
            { getFilteredResponses().length === 1 && renderResponseFull(getFilteredResponses()[0], 0) }

            </> }

            { view === 'individuals' && form && responsesLoaded && responses.length === 0 &&
              <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Encara no hi ha respostes</div>
            }
        </div>
      );
}

export default FormResponses;
